/**
 * Centralized route constants for the application
 */

/**
 * Public routes that don't require authentication
 * These routes should be accessible to both authenticated and non-authenticated users
 */
export const PUBLIC_ROUTES = [
  '/',
  '/come-funziona',
  '/faq',
  '/privacy',
  '/termini',
  '/login',
  '/create-group',
  '/group-selection',
  '/auth/verify'
] as const;

/**
 * Route patterns for public routes (for dynamic routes like /auth/verify/:token)
 */
export const PUBLIC_ROUTE_PATTERNS = [
  '/auth/verify/'
] as const;

/**
 * Check if a given path is a public route
 * @param pathname - The pathname to check
 * @returns true if the path is public, false otherwise
 */
export const isPublicRoute = (pathname: string): boolean => {
  // Check exact matches
  if (PUBLIC_ROUTES.includes(pathname as any)) {
    return true;
  }
  
  // Check pattern matches
  return PUBLIC_ROUTE_PATTERNS.some(pattern => pathname.startsWith(pattern));
};

/**
 * Extract the first segment of a path for route matching
 * @param pathname - The pathname to extract from
 * @returns the first segment or null
 */
export const getFirstPathSegment = (pathname: string): string | null => {
  const pathSegments = pathname.replace(/^\/+/, '').split('/');
  return pathSegments[0] || null;
};

/**
 * Check if a path segment corresponds to a public route
 * @param segment - The path segment to check
 * @returns true if the segment is public, false otherwise
 */
export const isPublicRouteSegment = (segment: string | null): boolean => {
  if (!segment) return true; // Root path
  
  // Extract segments from PUBLIC_ROUTES instead of duplicating
  const publicSegments = PUBLIC_ROUTES
    .filter(route => route !== '/') // Exclude root
    .map(route => route.replace('/', '')) // Remove leading slash
    .concat(['auth']); // Add auth for /auth/verify pattern
  
  return publicSegments.includes(segment);
};

/**
 * Reserved slugs that cannot be used as group slugs
 * These are the first segments of public routes
 */
export const RESERVED_SLUGS = PUBLIC_ROUTES
  .filter(route => route !== '/' && !route.includes(':')) // Exclude root and dynamic routes
  .map(route => route.replace(/^\//, '').split('/')[0]) // Get first segment
  .concat(['auth']); // Add auth for /auth/verify pattern

/**
 * Check if a slug is reserved and cannot be used as a group slug
 * @param slug - The slug to check
 * @returns true if the slug is reserved, false otherwise
 */
export const isReservedSlug = (slug: string): boolean => {
  return RESERVED_SLUGS.includes(slug.toLowerCase());
};